export type SpeechRecognitionLike = {
  readonly lang: string;
  readonly continuous: boolean;
  readonly interimResults: boolean;
  readonly onresult: ((event: SpeechRecognitionResultEventLike) => void) | null;
  readonly onerror: (() => void) | null;
  readonly onend: (() => void) | null;
  readonly start: () => void;
  readonly stop: () => void;
};

export type SpeechRecognitionResultEventLike = {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    readonly item: (index: number) =>
      | {
          readonly isFinal: boolean;
          readonly length: number;
          readonly item: (index: number) => { readonly transcript: string };
        }
      | undefined;
  };
};

type SpeechWindow = Window & {
  readonly SpeechRecognition?: new () => SpeechRecognitionLike;
  readonly webkitSpeechRecognition?: new () => SpeechRecognitionLike;
};

export function speechRecognitionConstructor(): (new () => SpeechRecognitionLike) | undefined {
  const speechWindow = window as SpeechWindow;
  return speechWindow.SpeechRecognition ?? speechWindow.webkitSpeechRecognition;
}

export function isSpeechDictationSupported(): boolean {
  return speechRecognitionConstructor() !== undefined;
}

export function startSpeechDictation(handlers: {
  readonly onTranscript: (text: string) => void;
  readonly onStop: () => void;
}): SpeechRecognitionLike | undefined {
  const Recognition = speechRecognitionConstructor();
  if (Recognition === undefined) {
    return undefined;
  }
  const recognition = new Recognition();
  Object.assign(recognition, {
    lang: 'fr-FR',
    continuous: true,
    interimResults: false,
    onresult: (event: SpeechRecognitionResultEventLike) => {
      const spoken = finalTranscriptFromEvent(event);
      if (spoken === '') {
        return;
      }
      handlers.onTranscript(spoken);
    },
    onerror: handlers.onStop,
    onend: handlers.onStop,
  });
  recognition.start();
  return recognition;
}

export function stopSpeechDictation(recognition: SpeechRecognitionLike | undefined): void {
  if (recognition === undefined) {
    return;
  }
  recognition.stop();
}

export function appendDictationTranscript(current: string, transcript: string): string {
  const next = transcript.trim();
  if (next === '') {
    return current;
  }
  if (current.trim() === '') {
    return next;
  }
  if (current.endsWith(' ') || current.endsWith('\n')) {
    return `${current}${next}`;
  }
  return `${current} ${next}`;
}

export function finalTranscriptFromEvent(event: SpeechRecognitionResultEventLike): string {
  const parts: string[] = [];
  Array.from({ length: event.results.length }, (_, index) => index)
    .filter((index) => index >= event.resultIndex)
    .forEach((index) => {
      const result = event.results.item(index);
      if (result === undefined || !result.isFinal || result.length === 0) {
        return;
      }
      const alternative = result.item(0);
      parts.push(alternative.transcript);
    });
  return parts.join(' ').trim();
}
